import { useEffect, useRef, useState } from "react";
import { api, apiError, imgUrl } from "../lib/api";
import { toast } from "sonner";
import { X, Undo2, Trash2, Pencil, Save, Wand2, Type as TypeIcon, Ruler } from "lucide-react";

const COLORS = [
  { name: "Red", value: "#dc2626" },
  { name: "Blue", value: "#2563eb" },
  { name: "Black", value: "#111111" },
  { name: "Green", value: "#16a34a" },
];

const SIZES = [2, 4, 8];
const CANVAS_SIZE = 900;

export default function FlatEditor({ open, onClose, techpackId, which, imageUrl, poms = [], baseSize = "M", existingCallouts = [], onSaved }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [tool, setTool] = useState("pen"); // pen | text | callout
  const [color, setColor] = useState(COLORS[0].value);
  const [size, setSize] = useState(SIZES[1]);
  const [strokes, setStrokes] = useState([]); // {type:"pen"|"text", color, size, points|text, x?, y?}
  const [callouts, setCallouts] = useState([]); // {id, pom_id, pom_code, x, y, label_x, label_y}
  const [pendingPos, setPendingPos] = useState(null); // canvas coords awaiting POM pick
  const [draggingLabel, setDraggingLabel] = useState(null); // callout id currently being dragged
  const [current, setCurrent] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [saving, setSaving] = useState(false);
  const [savingCallouts, setSavingCallouts] = useState(false);
  const [applyingAi, setApplyingAi] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStrokes([]);
    setCurrent(null);
    setPrompt("");
    setPendingPos(null);
    setCallouts(existingCallouts.map((c) => ({ ...c })));
    setImgLoaded(false);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;
      setImgLoaded(true);
    };
    img.src = imgUrl(imageUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imageUrl]);

  useEffect(() => {
    if (!imgLoaded) return;
    redraw();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgLoaded, strokes, current, callouts]);

  const imageBounds = () => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return null;
    const ratio = Math.min(canvas.width / img.width, canvas.height / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    return { x: (canvas.width - dw) / 2, y: (canvas.height - dh) / 2, w: dw, h: dh };
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const b = imageBounds();
    ctx.drawImage(imgRef.current, b.x, b.y, b.w, b.h);
    const all = current ? [...strokes, current] : strokes;
    for (const s of all) {
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      ctx.lineWidth = s.size;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (s.type === "pen" && s.points?.length) {
        ctx.beginPath();
        ctx.moveTo(s.points[0].x, s.points[0].y);
        for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
        ctx.stroke();
      } else if (s.type === "text") {
        ctx.font = `${s.size * 5}px Inter, sans-serif`;
        ctx.fillText(s.text, s.x, s.y);
      }
    }
    // Draw callouts (marker + leader line + label pill)
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    callouts.forEach((co, i) => {
      const cx = b.x + co.x * b.w;
      const cy = b.y + co.y * b.h;
      const lx = b.x + (co.label_x ?? co.x) * b.w;
      const ly = b.y + (co.label_y ?? co.y) * b.h;
      // Leader dashed line
      ctx.setLineDash([6, 5]);
      ctx.strokeStyle = "#111111";
      ctx.lineWidth = 1.6;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(lx, ly);
      ctx.stroke();
      ctx.setLineDash([]);
      // Marker
      ctx.beginPath();
      ctx.arc(cx, cy, 20, 0, Math.PI * 2);
      ctx.fillStyle = "#111111";
      ctx.fill();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 3;
      ctx.stroke();
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 18px Inter, sans-serif";
      ctx.fillText(String(i + 1), cx, cy);
      // Label pill
      const pom = poms.find((p) => p.id === co.pom_id) || poms.find((p) => p.code === co.pom_code);
      const val = pom?.measurements?.[baseSize];
      const labelText = `${co.pom_code || "?"}${val != null ? ` ${val}cm` : ""}`;
      ctx.font = "bold 16px Inter, sans-serif";
      const tw = ctx.measureText(labelText).width;
      const pw = tw + 26;
      const ph = 30;
      const px = lx - pw / 2;
      const py = ly - ph / 2;
      ctx.fillStyle = "#ffffff";
      ctx.strokeStyle = "#111111";
      ctx.lineWidth = 1.5;
      roundRect(ctx, px, py, pw, ph, 8);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#111111";
      ctx.fillText(labelText, lx, ly);
    });
    ctx.restore();
  };

  const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  };

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const findLabelAt = (p) => {
    const b = imageBounds();
    if (!b) return null;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    for (let i = callouts.length - 1; i >= 0; i--) {
      const co = callouts[i];
      const lx = b.x + (co.label_x ?? co.x) * b.w;
      const ly = b.y + (co.label_y ?? co.y) * b.h;
      const pom = poms.find((x) => x.id === co.pom_id) || poms.find((x) => x.code === co.pom_code);
      const val = pom?.measurements?.[baseSize];
      const labelText = `${co.pom_code || "?"}${val != null ? ` ${val}cm` : ""}`;
      ctx.font = "bold 16px Inter, sans-serif";
      const tw = ctx.measureText(labelText).width;
      const pw = tw + 26;
      const ph = 30;
      if (p.x >= lx - pw / 2 && p.x <= lx + pw / 2 && p.y >= ly - ph / 2 && p.y <= ly + ph / 2) {
        return co.id;
      }
    }
    return null;
  };

  const onDown = (e) => {
    e.preventDefault();
    const p = getPos(e);
    if (tool === "callout") {
      const hit = findLabelAt(p);
      if (hit) {
        setDraggingLabel(hit);
        return;
      }
      const b = imageBounds();
      if (!b) return;
      if (p.x < b.x || p.x > b.x + b.w || p.y < b.y || p.y > b.y + b.h) return;
      setPendingPos({
        canvas: p,
        norm: { x: (p.x - b.x) / b.w, y: (p.y - b.y) / b.h },
      });
      return;
    }
    if (tool === "pen") {
      setCurrent({ type: "pen", color, size, points: [p] });
    } else if (tool === "text") {
      const text = window.prompt("Enter annotation text");
      if (text) setStrokes([...strokes, { type: "text", color, size, x: p.x, y: p.y, text }]);
    }
  };

  const onMove = (e) => {
    if (draggingLabel) {
      const p = getPos(e);
      const b = imageBounds();
      if (!b) return;
      const nx = Math.max(0.02, Math.min(0.98, (p.x - b.x) / b.w));
      const ny = Math.max(0.02, Math.min(0.98, (p.y - b.y) / b.h));
      setCallouts(callouts.map((c) => c.id === draggingLabel ? { ...c, label_x: nx, label_y: ny } : c));
      return;
    }
    if (!current) return;
    const p = getPos(e);
    setCurrent({ ...current, points: [...current.points, p] });
  };

  const onUp = () => {
    if (draggingLabel) {
      setDraggingLabel(null);
      return;
    }
    if (current) {
      setStrokes([...strokes, current]);
      setCurrent(null);
    }
  };

  const undo = () => setStrokes(strokes.slice(0, -1));
  const clearAll = () => setStrokes([]);
  const removeCallout = (id) => setCallouts(callouts.filter((c) => c.id !== id));

  const addCalloutForPom = (pomOrCustom) => {
    if (!pendingPos) return;
    const id = (window.crypto?.randomUUID?.() || `co_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    const { x, y } = pendingPos.norm;
    // Auto-place label outside the garment horizontally
    const label_x = Math.max(0.05, Math.min(0.95, x < 0.5 ? x + 0.22 : x - 0.22));
    const label_y = Math.max(0.05, Math.min(0.95, y));
    let co;
    if (pomOrCustom.custom) {
      const code = window.prompt("Custom marker code (e.g. C1)");
      if (!code) { setPendingPos(null); return; }
      co = { id, pom_id: null, pom_code: code, x, y, label_x, label_y };
    } else {
      co = { id, pom_id: pomOrCustom.id, pom_code: pomOrCustom.code, x, y, label_x, label_y };
    }
    setCallouts([...callouts, co]);
    setPendingPos(null);
  };

  const canvasToB64WithoutCallouts = () => {
    // Re-render on an offscreen canvas without callouts, then export
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return canvas.toDataURL("image/png");
    const off = document.createElement("canvas");
    off.width = canvas.width;
    off.height = canvas.height;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, off.width, off.height);
    const img = imgRef.current;
    const ratio = Math.min(off.width / img.width, off.height / img.height);
    const dw = img.width * ratio;
    const dh = img.height * ratio;
    ctx.drawImage(img, (off.width - dw) / 2, (off.height - dh) / 2, dw, dh);
    for (const s of strokes) {
      ctx.strokeStyle = s.color;
      ctx.fillStyle = s.color;
      ctx.lineWidth = s.size;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (s.type === "pen" && s.points?.length) {
        ctx.beginPath();
        ctx.moveTo(s.points[0].x, s.points[0].y);
        for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
        ctx.stroke();
      } else if (s.type === "text") {
        ctx.font = `${s.size * 5}px Inter, sans-serif`;
        ctx.fillText(s.text, s.x, s.y);
      }
    }
    return off.toDataURL("image/png");
  };

  const saveCallouts = async () => {
    setSavingCallouts(true);
    try {
      const payload = callouts.map(({ id, pom_id, pom_code, x, y, label_x, label_y }) => ({ id, pom_id, pom_code, x, y, label_x, label_y }));
      const res = await api.post(`/techpacks/${techpackId}/callouts`, { which, callouts: payload });
      toast.success(`${payload.length} callout${payload.length !== 1 ? "s" : ""} saved`);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSavingCallouts(false);
    }
  };

  const saveAnnotations = async () => {
    setSaving(true);
    try {
      const data = canvasToB64WithoutCallouts();
      const res = await api.post(`/techpacks/${techpackId}/save-flat`, {
        which, image_b64: data,
      });
      // Also persist callouts if any were added
      if (callouts.length) {
        await api.post(`/techpacks/${techpackId}/callouts`, {
          which,
          callouts: callouts.map(({ id, pom_id, pom_code, x, y, label_x, label_y }) => ({ id, pom_id, pom_code, x, y, label_x, label_y })),
        });
      }
      toast.success("Flat saved");
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const applyAi = async () => {
    if (!prompt.trim()) {
      toast.error("Add a prompt describing what to change");
      return;
    }
    setApplyingAi(true);
    try {
      const data = canvasToB64WithoutCallouts();
      const res = await api.post(`/techpacks/${techpackId}/edit-flat`, {
        which, prompt, image_b64: data,
      });
      toast.success("AI re-drew the flat");
      onSaved?.(res.data);
      onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setApplyingAi(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 flex items-center justify-center p-4" data-testid="flat-editor">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <Pencil size={16} className="text-zinc-950" />
            <h2 className="font-heading text-lg font-bold">Edit {which} flat</h2>
            <span className="text-xs text-zinc-400 hidden sm:inline">Draw on the flat, or ask AI to redraw with a prompt</span>
          </div>
          <button onClick={onClose} data-testid="flat-editor-close" className="p-1.5 rounded hover:bg-zinc-100 text-zinc-500"><X size={16} /></button>
        </div>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_280px] overflow-hidden">
          <div className="p-4 flex flex-col items-center justify-center bg-zinc-50 overflow-auto relative">
            <canvas
              ref={canvasRef}
              width={CANVAS_SIZE}
              height={CANVAS_SIZE}
              onMouseDown={onDown}
              onMouseMove={onMove}
              onMouseUp={onUp}
              onMouseLeave={onUp}
              onTouchStart={(e) => onDown(e.touches[0])}
              onTouchMove={(e) => onMove(e.touches[0])}
              onTouchEnd={onUp}
              data-testid="flat-editor-canvas"
              className="w-full max-w-[600px] aspect-square border border-zinc-200 rounded-lg bg-white cursor-crosshair"
              style={{ touchAction: "none" }}
            />
            {pendingPos && (
              <div className="absolute inset-0 bg-zinc-950/40 flex items-center justify-center p-4 z-10" data-testid="pom-picker" onClick={() => setPendingPos(null)}>
                <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col overflow-hidden">
                  <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                    <p className="text-sm font-heading font-bold">Pick a measurement</p>
                    <button onClick={() => setPendingPos(null)} className="p-1 rounded hover:bg-zinc-100 text-zinc-500"><X size={14} /></button>
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {poms.length === 0 && <p className="text-xs text-zinc-400 p-4">No POMs on this techpack yet. Add measurements in the Sizes/POM tab first.</p>}
                    {poms.map((p) => (
                      <button key={p.id || p.code} data-testid={`pom-pick-${p.code}`} onClick={() => addCalloutForPom(p)}
                        className="w-full text-left px-4 py-2.5 hover:bg-zinc-100 flex items-center gap-3 border-b border-zinc-100 last:border-0 transition-colors">
                        <span className="text-xs font-bold w-10">{p.code}</span>
                        <span className="text-xs text-zinc-500 flex-1 truncate">{p.description}</span>
                        <span className="text-xs text-zinc-950 font-medium">{p.measurements?.[baseSize] ?? "-"}cm</span>
                      </button>
                    ))}
                  </div>
                  <div className="p-3 border-t border-zinc-200">
                    <button data-testid="pom-pick-custom" onClick={() => addCalloutForPom({ custom: true })}
                      className="w-full text-xs text-zinc-500 hover:text-zinc-950 border border-dashed border-zinc-300 rounded-md px-3 py-2 hover:bg-zinc-100 transition-colors">
                      + Custom marker code
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="border-l border-zinc-200 p-4 overflow-y-auto space-y-5">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Tool</p>
              <div className="grid grid-cols-3 gap-2">
                <button onClick={() => setTool("pen")} data-testid="tool-pen"
                  className={`inline-flex items-center justify-center gap-1.5 border rounded-md px-2 py-2 text-xs font-medium transition-colors ${tool === "pen" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
                  <Pencil size={13} /> Pen
                </button>
                <button onClick={() => setTool("text")} data-testid="tool-text"
                  className={`inline-flex items-center justify-center gap-1.5 border rounded-md px-2 py-2 text-xs font-medium transition-colors ${tool === "text" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
                  <TypeIcon size={13} /> Text
                </button>
                <button onClick={() => setTool("callout")} data-testid="tool-callout"
                  className={`inline-flex items-center justify-center gap-1.5 border rounded-md px-2 py-2 text-xs font-medium transition-colors ${tool === "callout" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
                  <Ruler size={13} /> Callout
                </button>
              </div>
              {tool === "callout" && (
                <p className="text-[11px] text-zinc-400 mt-2">Click any point on the flat to drop a measurement marker. Drag the label to reposition it.</p>
              )}
            </div>

            {tool === "callout" ? (
              <div className="space-y-3" data-testid="callout-panel">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Callouts ({callouts.length})</p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {callouts.length === 0 && <p className="text-[11px] text-zinc-400">No callouts yet. Click on the flat to add one.</p>}
                    {callouts.map((co, i) => {
                      const pom = poms.find((p) => p.id === co.pom_id) || poms.find((p) => p.code === co.pom_code);
                      const val = pom?.measurements?.[baseSize];
                      return (
                        <div key={co.id} className="flex items-center gap-2 border border-zinc-200 rounded-md px-2 py-1.5" data-testid={`callout-item-${i}`}>
                          <span className="w-5 h-5 rounded-full bg-zinc-950 text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                          <span className="text-xs font-medium">{co.pom_code || "?"}</span>
                          <span className="text-[11px] text-zinc-500 truncate flex-1">{pom?.description || "custom"}{val ? ` · ${val}cm` : ""}</span>
                          <button onClick={() => removeCallout(co.id)} data-testid={`callout-remove-${i}`} className="text-zinc-400 hover:text-red-600 shrink-0"><X size={12} /></button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <button data-testid="callouts-save" onClick={saveCallouts} disabled={savingCallouts}
                  className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                  <Save size={13} /> {savingCallouts ? "Saving…" : "Save callouts"}
                </button>
                <p className="text-[11px] text-zinc-400">Callouts render in the PDF as numbered markers with a legend of POM code + measurement at base size {baseSize}.</p>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Color</p>
                  <div className="flex gap-2">
                    {COLORS.map((cc) => (
                      <button key={cc.value} onClick={() => setColor(cc.value)} title={cc.name}
                        data-testid={`tool-color-${cc.name.toLowerCase()}`}
                        className={`w-8 h-8 rounded-full border-2 transition-transform ${color === cc.value ? "border-zinc-950 scale-110" : "border-zinc-200"}`}
                        style={{ backgroundColor: cc.value }} />
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium mb-2">Stroke</p>
                  <div className="flex gap-2">
                    {SIZES.map((sz) => (
                      <button key={sz} onClick={() => setSize(sz)} data-testid={`tool-size-${sz}`}
                        className={`flex-1 border rounded-md py-2 text-xs font-medium transition-colors ${size === sz ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 text-zinc-600 hover:bg-zinc-100"}`}>
                        {sz === 2 ? "S" : sz === 4 ? "M" : "L"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button onClick={undo} disabled={!strokes.length} data-testid="tool-undo"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-300 rounded-md px-3 py-2 text-xs font-medium hover:bg-zinc-100 disabled:opacity-40">
                    <Undo2 size={13} /> Undo
                  </button>
                  <button onClick={clearAll} disabled={!strokes.length} data-testid="tool-clear"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-300 rounded-md px-3 py-2 text-xs font-medium hover:bg-zinc-100 disabled:opacity-40">
                    <Trash2 size={13} /> Clear
                  </button>
                </div>
              </>
            )}

            <div className="border-t border-zinc-200 pt-4 space-y-2">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Ask AI to redraw</p>
              <textarea
                data-testid="flat-editor-prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Add a chest pocket on the left, shorten the sleeves, add a v-neck instead of a crew"
                rows={4}
                className="w-full border border-zinc-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-zinc-950"
              />
              <p className="text-[11px] text-zinc-400">Your annotations act as visual hints. The AI redraws a clean CAD with your changes.</p>
              <button data-testid="flat-editor-apply-ai" onClick={applyAi} disabled={applyingAi || !prompt.trim()}
                className="w-full inline-flex items-center justify-center gap-2 bg-zinc-950 text-white px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                <Wand2 size={14} /> {applyingAi ? "Redrawing…" : "Apply with AI"}
              </button>
            </div>

            <div className="border-t border-zinc-200 pt-4">
              <button data-testid="flat-editor-save" onClick={saveAnnotations} disabled={saving}
                className="w-full inline-flex items-center justify-center gap-2 border border-zinc-950 text-zinc-950 px-4 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-950 hover:text-white disabled:opacity-40 transition-colors">
                <Save size={14} /> {saving ? "Saving…" : "Save annotations as flat"}
              </button>
              <p className="text-[11px] text-zinc-400 mt-2">Bakes the drawing into the flat image (no AI call).</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
